import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { openConsultationModal } from "./ConsultationModal";
import { ArrowRight } from "lucide-react";
import { LineReveal, Magnetic, Reveal, HeroTypewriter } from "./motion";

function Particles() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let w, h, raf;
    const dots = [];
    const resize = () => {
      w = canvas.width = canvas.offsetWidth;
      h = canvas.height = canvas.offsetHeight;
    };
    resize();
    for (let i = 0; i < 70; i++) {
      dots.push({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: Math.random() * 1.8 + 0.4,
      });
    }
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      dots.forEach((d) => {
        d.x += d.vx;
        d.y += d.vy;
        if (d.x < 0 || d.x > w) d.vx *= -1;
        if (d.y < 0 || d.y > h) d.vy *= -1;
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(255,90,31,0.6)";
        ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };
    draw();
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full pointer-events-none" />;
}

export const Hero = () => {
  const navigate = useNavigate();

  return (
    <section
      data-testid="hero"
      className="section-dark relative min-h-[92svh] lg:min-h-[100svh] flex flex-col justify-center items-center overflow-hidden grid-bg-dark pt-20 pb-12 sm:pt-24 sm:pb-16"
    >
      {/* Aurora Ambient Lighting */}
      <div className="blob w-[600px] h-[600px] bg-[#FF5A1F] -top-40 -left-40 animate-aurora opacity-40 pointer-events-none" />
      <div
        className="blob w-[460px] h-[460px] bg-[#c23c00] top-1/3 -right-32 animate-aurora opacity-30 pointer-events-none"
        style={{ animationDelay: "3s" }}
      />
      <Particles />

      {/* Centered Middle Text Section — shifted slightly upwards for optical balance */}
      <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 w-full flex flex-col items-center justify-center text-center -mt-6 sm:-mt-10 lg:-mt-12">
        {/* Main Heading in Middle */}
        <h1 className="font-display font-bold tracking-tight text-white text-3xl sm:text-6xl lg:text-7xl leading-[1.08] max-w-4xl mx-auto text-center">
          <HeroTypewriter delay={0.2} />
        </h1>

        {/* Subtitle in Middle — shifted comfortably below typewriter heading */}
        <Reveal delay={0.6} className="mt-8 sm:mt-11 lg:mt-12 max-w-2xl mx-auto text-center">
          <p className="text-base sm:text-xl text-neutral-300 leading-relaxed text-center">
            We help you create an AI-driven ecosystem from strategy to implementation, so your business can focus on exponential growth.
          </p>
        </Reveal>

        {/* Action Buttons in Middle */}
        <Reveal
          delay={0.8}
          className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-3.5 sm:gap-4 flex-wrap w-full"
        >
          <Magnetic className="w-full sm:w-auto">
            <button
              data-testid="hero-consult-btn"
              onClick={() => openConsultationModal()}
              className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 sm:py-4 rounded-full bg-[#FF5A1F] text-white font-semibold hover:bg-[#ff6d38] transition-all shadow-[0_8px_25px_-4px_rgba(255,90,31,0.5)] cursor-pointer"
            >
              <span>Book Free Consultation</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </Magnetic>

          <Magnetic className="w-full sm:w-auto">
            <button
              data-testid="hero-services-btn"
              onClick={() => navigate("/services")}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 sm:py-4 rounded-full glass-dark text-white font-semibold hover:border-[#FF5A1F] transition-all cursor-pointer"
            >
              <span>Explore Services</span>
            </button>
          </Magnetic>

          <Magnetic className="w-full sm:w-auto">
            <button
              data-testid="hero-work-btn"
              onClick={() => navigate("/portfolio")}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 sm:py-4 rounded-full glass-dark text-white font-semibold hover:border-[#FF5A1F] transition-all cursor-pointer"
            >
              <span>Check Our Work</span>
            </button>
          </Magnetic>
        </Reveal>
      </div>
    </section>
  );
};

export default Hero;
